import type { DetailedRisk, LandData } from '../types/siteRag';
import type { FloodScreeningCoverage } from '../types/floodScreening';

/** Accept both provider/SED evidence and reconstructed report land. */
export interface LandFloodInput {
  floodRisk?: string;
  floodZone?: string;
  femaZone?: string;
  sfha?: boolean;
  isSpecialFloodHazard?: boolean;
  screeningCoverage?: FloodScreeningCoverage;
  floodCoverage?: FloodScreeningCoverage;
  areaAnalysis?: { unmappedFraction?: number };
}

export function resolveLandFloodRisk(input?: LandFloodInput | null): LandData['floodRisk'] {
  // Incomplete coverage never erases observed positive exposure.
  if (input?.sfha === true || input?.isSpecialFloodHazard === true || input?.floodRisk === 'High') return 'High';
  const coverage = input?.screeningCoverage ?? input?.floodCoverage;
  if ((coverage && coverage.status !== 'complete')
    || (input?.areaAnalysis?.unmappedFraction ?? 0) > 1e-6
    || /^(UNKNOWN|UNMAPPED|UNDETERMINED)$/i.test(input?.floodZone ?? input?.femaZone ?? '')) return 'Unknown';
  const risk = input?.floodRisk;
  return risk === 'Low' || risk === 'Low-Medium' || risk === 'Medium' ? risk : 'Unknown';
}

export function floodRiskKpi(input?: LandFloodInput | null) {
  const risk = resolveLandFloodRisk(input);
  return { key: 'Flood risk', value: risk,
    tone: risk === 'High' ? 'negative' as const : risk === 'Low' ? 'positive' as const
      : risk === 'Unknown' ? 'default' as const : 'warning' as const };
}

/** Unscored flood gaps are emitted by buildEvidenceGapRisks, not assigned an
 * invented Low/Medium/High likelihood in the graded development-risk list. */
export function floodDevelopmentRisks(input?: LandFloodInput | null): DetailedRisk[] {
  const risk = resolveLandFloodRisk(input);
  if (risk === 'Unknown') return [];
  const coverage = input?.screeningCoverage ?? input?.floodCoverage;
  return [{ category: 'Flood Risk',
    riskLevel: risk === 'High' ? 'High' : risk === 'Low' ? 'Low' : 'Medium',
    impactLevel: risk === 'High' ? 'Red' : risk === 'Low' ? 'Green' : 'Amber',
    reason: `Flood screening risk: ${risk}.${coverage && coverage.status !== 'complete' ? ` Coverage is ${coverage.status}: ${coverage.reason}` : ''}`,
    action: risk === 'High' ? 'Commission flood assessment.' : risk === 'Low' ? 'Confirm the mapped flood basis remains current for the development area.' : 'Review mapped flood exposure and required site mitigation with a flood specialist.',
  }];
}
